import { ISO8601 } from '../../support/ISO8601';
import { State } from '@meraki-internal/state';
import { replaceItem } from '../../utils/arrays';
import { BufferedEventBus } from '../../utils/BufferedEventBus';
import { RECORDING_SERVICE_STATUS_EVENT, RecordingService } from './RecordingService';
import { CaremapNotesModelFactory } from '../../notes/model/CaremapNotesModelFactory';
import { Logger } from '../../support/Logger';
import { CareMapState } from '../../caremap/CareMapState';
import { IFreeTierLimits } from '../../innerhive-plus/revenue-cat/IFreeTierLimits';
import { RevenueCatModel } from '../../innerhive-plus/revenue-cat/RevenueCatModel';
import { APP_CALL_STATE_CHANGE, APP_FOREGROUNDED } from '../../AppContainer';
import { CurrentRecordingStatus, VoiceRecorder } from 'capacitor-voice-recorder';
import { PhoneState } from 'capacitor-plugin-incoming-call';
import { isPlatform } from '@ionic/react';
import dayjs from 'dayjs';

export type IRecordingStatus = 'recording-complete' | 'uploading' | 'uploaded' | 'upload-failed' | 'transcribed' | 'transcribe-failed' | 'summarized' | 'summarize-failed';

export interface IRecordingSummary {
    title: string;
    participants?: string[];
    shortSummary: string; // listing
    highLevelSummary: string; // overview of what AI generates
    detailedSummary?: { // bulk of what AI generates
        keyTopicsDiscussed?: string[];
        decisionsMade?: string[];
        instructionsOrRecommendations?: string[];
        nextStepsAndFollowups?: string[];
    };
    actionItems?: {
        id: string;
        description: string;
    }[];
}

export interface IRecording {
    id: string;
    fileName: string;

    file: ArrayBuffer;
    url: string;

    noteId: string;
    status: IRecordingStatus;

    appointmentId?: string;

    summary?: IRecordingSummary;
    transcription?: string;
    skipAIProcessing?: boolean;

    mimeType: string;
    msDuration: number;
    byteLength: number;

    created: ISO8601;
};

export type IRecordingData = Pick<IRecording, 'mimeType' | 'msDuration' | 'file'>;

type ICurrentRecordingExtras = {
    appointmentId?: string;
    initiatedFrom?: string;
};

type IRecordingState = {
    isRecording: boolean;
    recordings: IRecording[];
    recordingInterrupted?: boolean;
    recordingInterruptedOutgoing?: boolean;
    showRecordingInterruptedAlert? : boolean;

    // so things like buttons in Alerts can use the Recorder
    forceStartRecording?: boolean;
    currentRecordingExtras?: ICurrentRecordingExtras;
};
export class RecordingState extends State<IRecordingState> {
    static inject = () => [
        RecordingService,
        BufferedEventBus,
        CaremapNotesModelFactory,
        CareMapState,
        RevenueCatModel,
        Logger
    ];

    constructor(
        private service: RecordingService,
        private events: BufferedEventBus,
        private notesFactory: CaremapNotesModelFactory,
        private careMapState: CareMapState,
        private revenueCat: RevenueCatModel,
        private logger: Logger
    ) {
        super({
            isRecording: false,
            recordings: []
        });

        this.load();

        this.events.on(RECORDING_SERVICE_STATUS_EVENT, (payload: IRecording | undefined) => {
            this.onRecordingStatusUpdated(payload);
            this.updateInitialWizardAnswer(payload);
        });

        // handle phone calls
        if (isPlatform('capacitor') && isPlatform('ios')) {
            this.events.on(APP_CALL_STATE_CHANGE, async (payload: PhoneState | undefined) => {
                try {
                    const { status } = await VoiceRecorder.getCurrentStatus() as CurrentRecordingStatus;

                    if (status === 'RECORDING') {
                        if (payload?.callState === 'RINGING') {
                            this.handleRecordingInterrupted();
                        } else if (payload?.callState === 'OUTGOING') {
                            this.setState({ recordingInterruptedOutgoing: true });
                        }
                    }
                } catch (error) {
                    throw error;
                }
            });

            // handle outgoing call on app foreground,
            // otherwise, if handling it above, recording was stuck in
            // Recording Complete but didn't exist / wasn't saved in indexedDB
            this.events.on(APP_FOREGROUNDED, async () => {
                if (this.state.recordingInterruptedOutgoing) {
                    this.handleRecordingInterrupted();
                }
            });
        }
    };

    private hasProcessedLocalRecordings = false;

    private NUM_RECORDINGS_QUESTION = 'num-recordings';
    private NUM_AI_PROCESSED_RECORDINGS_QUESTION = 'num-ai-processed-recordings';

    load = async () => {
        this.setState({ recordings: await this.service.getAll() });

        // TODO: forActiveCaremap throws an error for anonymous
        try {
            this.notesFactory.forActiveCaremap().subscribe(notesModel => {
                // only check on the first load and make sure notes loaded first
                if (!this.hasProcessedLocalRecordings && notesModel.hasLoaded) {
                    this.state.recordings.forEach(recording => {
                        const note = this.findNote(recording.noteId);

                        // cleanup potential recordings in indexedDB that have
                        // been uploaded and attached to notes, but for some reason weren't deleted
                        if (this.isUploadedToNote(recording)) {
                            if (this.isUploadedStatus(recording.status)) {
                                this.service.delete(recording.id);
                            }
                        } else {
                            if (note && recording.status === 'upload-failed') {
                                // TODO: sometimes local db has status == 'uploading'?!

                                // it has a note, but upload failed.
                                // it's still on the device, let's retry upload
                                this.service.upload(recording);
                            }
                        }

                        // if there is an ophaned recording, it is completed but not moved on or upload failed,
                        // it might be stuck in indexedDB so let's log it to Sentry
                        // doing here because need to make sure notesModel.hasLoaded before checking
                        if (localStorage.getItem('IS_AUTOMATED_TESTS') !== 'true' &&
                            dayjs(recording.created).isAfter('2025-07-07') &&
                            (!this.isUploadedToNote(recording) ||
                            recording.status === 'recording-complete' ||
                            recording.status === 'upload-failed')) {
                            this.logger.error('Potentially Orphaned Recording', recording);
                        }
                    });

                    // doing here because need to make sure notesModel.hasLoaded before checking
                    this.hasProcessedLocalRecordings = true;
                }
            });
        } catch (error) {}
    };

    private handleRecordingInterrupted = async () => {
        this.setState({ recordingInterrupted: true });

        // wait a sec before showing prompt
        await new Promise(res => setTimeout(res, 2000));
        this.setState({ showRecordingInterruptedAlert: true });
    };

    start = () => {
        this.setState({ isRecording: true });
    };

    stop = () => {
        this.setState({ isRecording: false });
    };

    isRecording = () => this.state.isRecording;

    add = async (recording: IRecordingData) => {
        const newRecording = await this.service.add(
            recording,
            {
                skipAIProcessing: this.shouldSkipAIProcessing(),
                appointmentId: this.state.currentRecordingExtras?.appointmentId
            }
        );

        this.setState({
            recordings: [...this.state.recordings, newRecording],
            currentRecordingExtras: undefined
        });
    };

    // assumes NotesModel.hasLoaded = true
    findNote = (noteId: string) => {
        const notes = this.notesFactory.forActiveCaremap().getNotes();
        return notes.find(n => n.noteId === noteId);
    };

    // assumes NotesModel.hasLoaded = true
    isUploadedToNote = (recording: IRecording) => {
        try {
            const recordingNote = this.findNote(recording.noteId);
            return recordingNote?.attachments.find(a => a.url === recording.url) ? true : false;
        } catch (e) {
            // we can't have this throw because it's used when RecordingState initializes,
            // and it will throw if there's no active caremap, so return false instead...
            // this might result in bogus "orphaned" messages, but will ensure we don't
            // delete the recording prematurely
            return false;
        }
    };

    isUploadedStatus = (status: IRecordingStatus | undefined) => {
        return status && status !== 'recording-complete' && status !== 'uploading' && status !== 'upload-failed';
    };

    onRecordingStatusUpdated = async (payload: IRecording | undefined) => {
        if (payload) {
            // this errors out if it wasn't ever saved to the server successfully
            try {
                // get the note
                // TODO: is it possible the careMap.getNotes() has a new note in state, but it's not saved to the server yet??
                //      I THINK SO, I THINK IT'S WHY I SEE THE 409 CONFLICT SOMETIMES.
                //      SAVE HASN'T COMPLETED, THE UPLOAD FINISHES AND IT TRIES TO EDIT / SAVE???
                const noteVM = await this.notesFactory.createEditForm(payload.noteId);
                let note = noteVM.getNote();

                // upldate otherAttributes.recording
                note = {
                    ...note,
                    otherAttributes: {
                        ...note.otherAttributes,
                        recording: payload
                    }
                };

                if (payload.status === 'uploaded') {
                    // add the attachment
                    note = {
                        ...note,
                        attachments: [
                            ...note.attachments,
                            {
                                contentType: payload.mimeType,
                                fileName: payload.fileName,
                                url: payload.url
                            }
                        ]
                    };
                }

                noteVM.setNote(note);

                // skip certain Mixpanel tracking
                await noteVM.save({ skipTracking: true });

                if (payload.status === 'uploaded') {
                    // delete from inedexDB if the note has it attached
                    // TODO: is it possible the careMap.getNotes() has a new note in state, but it's not saved to the server yet??
                    if (this.isUploadedToNote(payload)) {
                        this.service.delete(payload.id);
                    }

                    // remove from state
                    this.setState({ recordings: this.state.recordings.filter(r => r.id !== payload.id) });
                } else {
                    this.setState({ recordings: replaceItem(this.state.recordings, payload, 'id') });
                }
            } catch (error) {
                console.log(error);
                this.logger.error(error);
            }
        }
    };

    updateInitialWizardAnswer = (payload: IRecording | undefined) => {
        if (payload?.status === 'uploading') {
            let numRecordings = this.getNumCareMapRecordings();
            this.careMapState.answerInitialWizardQuestion({
                questionId: this.NUM_RECORDINGS_QUESTION,
                answer: ++numRecordings
            });

            let numAIProcessedRecordings = this.getNumCareMapAIProcessedRecordings();
            this.careMapState.answerInitialWizardQuestion({
                questionId: this.NUM_AI_PROCESSED_RECORDINGS_QUESTION,
                answer: ++numAIProcessedRecordings
            });
        }
    };

    private getCareMap = () => {
        return this.careMapState.getActiveCaremap();
    };

    private getNumCareMapRecordings = (): number => {
        return this.getCareMap()?.initialWizardAnswers[this.NUM_RECORDINGS_QUESTION] || 0;
    };

    private isRecordingLimitReached = (limits: IFreeTierLimits) => {
        return this.getNumCareMapRecordings() >= limits.freeRecordings;
    };

    private getNumCareMapAIProcessedRecordings = (): number => {
        return this.getCareMap()?.initialWizardAnswers[this.NUM_AI_PROCESSED_RECORDINGS_QUESTION] || 0;
    };

    private shouldSkipAIProcessing = () => {
        return !this.revenueCat.hasInnerhivePlus() && this.isRecordingAILimitReached();
    };

    isRecordingAILimitReached = () => {
        const limits = this.revenueCat.getFreeTierLimits();
        return this.getNumCareMapAIProcessedRecordings() >= limits.freeRecordingAIProcessing;
    };

    requiresInnerhivePlus = (limits: IFreeTierLimits) => {
        return this.isRecordingLimitReached(limits);
    };

    getInnerhivePlusTriggerSource = () => {
        const limits = this.revenueCat.getFreeTierLimits();
        return this.isRecordingLimitReached(limits) ? 'recordings' : 'recording ai processing';
    };

    findRecordingForAppointment = (apptId: string): IRecording => {
        const notes = this.notesFactory.forActiveCaremap().getNotes();
        const noteForAppt = notes.find(note => note?.otherAttributes?.recording?.appointmentId === apptId);
        return noteForAppt?.otherAttributes.recording as IRecording;
    };

    updateCurrentRecordingExtras = (extras: Partial<ICurrentRecordingExtras>) => {
        this.setState({
            currentRecordingExtras: {...this.state.currentRecordingExtras, ...extras}
        });
    };
};
